import { useState } from 'react';
import { Brain } from './Brain';
import { Debate } from './Debate';
import { FullBrain } from './FullBrain';

export default function App() {
  const [maleFired, setMaleFired] = useState<string[]>([]);
  const [femaleFired, setFemaleFired] = useState<string[]>([]);
  const [sex, setSex] = useState<'male' | 'female'>('male');
  const [view, setView] = useState<'full' | 'region'>('full');

  const handleFiredChange = (m: string[], f: string[]) => {
    setMaleFired(m);
    setFemaleFired(f);
  };

  return (
    <div className="app">
      <header className="header">
        <h1>🪰 Banana — Drosophila Connectome Explorer</h1>
        <p className="subtitle">
          A deterministic, offline fruit-fly connectome explorer. Open the real whole-brain datasets at Janelia and FlyWire, or run a
          transparent 9-region leaky integrate-and-fire (LIF) simulation. No language model runs here, and no connectome data ships with
          this page — see the sources below.
        </p>
        <div className="stats-row">
          <div className="stat">
            <div className="value">166,700</div>
            <div className="label">male neurons <em>(external dataset)</em></div>
          </div>
          <div className="stat">
            <div className="value">6.24M</div>
            <div className="label">male connections <em>(external dataset)</em></div>
          </div>
          <div className="stat">
            <div className="value">158,262</div>
            <div className="label">female neurons <em>(external dataset)</em></div>
          </div>
          <div className="stat">
            <div className="value">0</div>
            <div className="label">connectome files bundled</div>
          </div>
        </div>
      </header>

      <section className="panel">
        <h2>
          {sex === 'male' ? '♂' : '♀'} Connectome view
          <span className="view-switcher">
            <button onClick={() => setView('full')} className={view === 'full' ? 'primary' : ''}>full brain</button>
            <button onClick={() => setView('region')} className={view === 'region' ? 'primary' : ''}>region demo</button>
            <button onClick={() => setSex('male')} className={sex === 'male' ? 'primary' : ''}>♂ male</button>
            <button onClick={() => setSex('female')} className={sex === 'female' ? 'primary' : ''}>♀ female</button>
          </span>
        </h2>
        {view === 'full' ? (
          <FullBrain sex={sex} />
        ) : (
          <Brain
            firedRegions={sex === 'male' ? maleFired : femaleFired}
            sex={sex}
          />
        )}
      </section>

      <section className="panel">
        <h2>💬 Fly Debate <span className="view-switcher"><em>deterministic, no LLM</em></span></h2>
        <p className="panel-note">
          The &ldquo;debate&rdquo; is arithmetic, not language. Your text is folded into two sensor values by an FNV-1a hash
          (<code>connectome.ts</code>); those values drive the same 9-region LIF network twice, once with male and once with female edge
          weights. The verdict below is a threshold on the difference between the two runs&rsquo; spike statistics. Identical text always
          produces an identical verdict, and different text is not understood — it is only hashed.
        </p>
        <Debate onFiredChange={handleFiredChange} />
      </section>

      <footer className="footer">
        <p>
          Sources (not bundled — opened at their official portals): MaleCNS v1.0, Google + HHMI, CC BY 4.0 · FlyWire BANC v888,
          FlyWire terms and citation requirements apply ·{' '}
          <a href="https://banana.maracuya.network" target="_blank" rel="noreferrer">banana.maracuya.network</a>
        </p>
        <p>
          This app ships no connectome dataset. <strong>Full Brain</strong> reads verified public dataset metadata (neuron and connection
          counts, licences) and links out to the official Janelia and FlyWire viewers, which host the ~1.1&nbsp;GB source graphs.{' '}
          <strong>Region Demo</strong> is a local 9-region LIF abstraction whose inter-region weights are hand-invented for illustration —
          plausible, not measured.
        </p>
      </footer>
    </div>
  );
}
